import { KNOWN_ROLES } from '../constants/roles';

const STORAGE_KEY = 'roleOverride';
const TTL_MS = 60 * 60 * 1000; // 1 hour
const CHANGE_EVENT = 'role-override-change';

function safeParse(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.roles) || typeof parsed.expiresAt !== 'number') {
      return null;
    }
    const cleanRoles = parsed.roles.filter(r => KNOWN_ROLES.includes(r));
    if (cleanRoles.length === 0) return null;
    return { roles: cleanRoles, expiresAt: parsed.expiresAt };
  } catch {
    return null;
  }
}

export function getRoleOverride() {
  if (typeof window === 'undefined') return null;
  const parsed = safeParse(window.localStorage.getItem(STORAGE_KEY));
  if (!parsed) return null;
  if (Date.now() > parsed.expiresAt) {
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
  return parsed;
}

export function setRoleOverride(roles) {
  if (typeof window === 'undefined') return;
  const cleanRoles = (Array.isArray(roles) ? roles : [roles]).filter(r => KNOWN_ROLES.includes(r));
  if (cleanRoles.length === 0) {
    clearRoleOverride();
    return;
  }
  const value = { roles: cleanRoles, expiresAt: Date.now() + TTL_MS };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export function clearRoleOverride() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

// Subscribe to override changes (same-tab CustomEvent + cross-tab storage event).
// Returns an unsubscribe fn.
export function subscribeRoleOverride(handler) {
  if (typeof window === 'undefined') return () => {};
  const onCustom = () => handler(getRoleOverride());
  const onStorage = (e) => {
    if (e.key === STORAGE_KEY) handler(getRoleOverride());
  };
  window.addEventListener(CHANGE_EVENT, onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
  };
}
